import { isBlank, isNone, isEmpty } from '@ember/utils';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { memberAction } from 'ember-api-actions';
import moment from 'moment';

import { timestamp } from 'later/utils/time-format';

import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import type AccountModel from 'later/models/account';
import type DeviceModel from 'later/models/device';
import type GramModel from 'later/models/gram';
import type GroupModel from 'later/models/group';
import type MembershipModel from 'later/models/membership';
import type MetaAdUserModel from 'later/models/meta-ad-user';
import type PostActivityModel from 'later/models/post-activity';

type UpdateEmailPayload = { email: string };

export default class UserModel extends Model {
  @attr('boolean', { defaultValue: false }) declare readonly admin: boolean;
  @attr('string', { defaultValue: '' }) declare readonly avatarUrl: string;
  @attr('boolean') declare readonly canAddUser?: boolean;
  @attr('boolean') declare readonly canSetProfilePermissions?: boolean;
  @attr('number') declare readonly createdTime?: number;
  @attr('string') declare currentPassword?: string;
  @attr('boolean', { defaultValue: false }) declare dev: boolean;
  @attr('string') declare readonly displayName?: string;
  @attr('string', { defaultValue: '' }) declare email: string;
  @attr('boolean', { defaultValue: false }) declare emailFollowUpNotifications: boolean;
  @attr('string') declare facebookToken?: string;
  @attr('string') declare facebookUid?: string;
  @attr('number') declare firstDay?: number;
  @attr('boolean', { defaultValue: false }) declare followUpPushNotifications: boolean;
  @attr('string') declare googleClientId?: string;
  @attr('boolean') declare readonly hasEncryptedPassword?: boolean;
  @attr('boolean') declare highPerformingContentEmailNotifications?: boolean;
  @attr('boolean') declare highPerformingContentPushNotifications?: boolean;
  @attr('boolean') declare highPerformingCommentsEmailNotifications?: boolean;
  @attr('boolean') declare highPerformingCommentsPushNotifications?: boolean;
  @attr('boolean', { defaultValue: false }) declare readonly isAccountOwner: boolean;
  // isNewSignUp: set by the server when a user signs up, updated by the client when the user completes the onboarding flow
  @attr('boolean', { defaultValue: false }) declare readonly isNewSignUp: boolean;
  @attr('string') declare readonly keenReadKey?: string;
  @attr('string') declare language?: string;
  @attr('number') declare readonly lastSignedIn?: number;
  @attr('number') declare loginsSinceScheduleReminder?: number;
  @attr('string') declare name?: string;
  @attr('boolean') declare otp_required_for_login?: boolean;
  @attr('boolean') declare owl?: boolean;
  @attr('string') declare linkedinToken?: string;
  @attr('number') declare readonly linkedinTokenExpiresTime?: number;
  @attr('string') declare password?: string;
  @attr('string') declare passwordConfirmation?: string;
  @attr('boolean') declare postFailedToAutoPublishEmailNotifications?: boolean;
  @attr('boolean') declare postReviewEmailNotifications?: boolean;
  @attr('boolean') declare receivedImportMediaEducation?: boolean;
  @attr('boolean') declare receivedInstagramStoriesEducation?: boolean;
  @attr('boolean') declare receivedMentionsEducation?: boolean;
  @attr('boolean') declare receivedMultiImageEducation?: boolean;
  @attr('boolean') declare receivedRegramEducation?: boolean;
  @attr('boolean') declare receivedSchedulingRewards?: boolean;
  @attr('boolean') declare receivedSearchByProfileEducation?: boolean;
  @attr('boolean') declare receivedTaggedEducation?: boolean;
  @attr('boolean') declare receivedYoutubeEducation?: boolean;
  @attr('string') declare referralCode?: string;
  @attr('number') declare referredById?: number;
  @attr('string') declare signupSource?: string;
  @attr('boolean') declare readonly showBanner?: boolean;
  @attr('number') declare testGroupId?: number;
  @attr('string') declare tiktokClickId?: string;
  @attr('string', { defaultValue: () => moment.tz.guess() }) timeZone?: string;
  @attr('string', { defaultValue: () => moment.tz.guess() }) readonly timeZoneIdentifier?: string;
  @attr('boolean') declare unconfirmed_two_factor?: boolean;
  @attr('string') declare readonly websocketToken?: string;

  @belongsTo('account', { async: true }) declare account: AsyncBelongsTo<AccountModel>;
  @hasMany('device', { async: true }) declare devices: AsyncHasMany<DeviceModel>;
  @hasMany('membership', { async: true }) declare memberships: AsyncHasMany<MembershipModel>;
  @belongsTo('meta-ad-user', { async: false, inverse: 'user' }) declare metaAdUser: MetaAdUserModel | null;
  @hasMany('gram', { async: true }) declare posts: AsyncHasMany<GramModel>;
  @hasMany('postActivity', { async: true }) declare postActivities: AsyncHasMany<PostActivityModel>;

  get canKeen(): boolean {
    return this.admin && !isEmpty(this.keenReadKey);
  }

  get firstName(): string {
    return this.name?.split(' ')[0] || '';
  }

  get lastName(): string {
    const names = this.name?.split(' ') || [];
    return names.slice(1).join(' ');
  }

  get secureAvatarUrl(): string {
    if (!isNone(this.avatarUrl)) {
      return this.avatarUrl.replace('http://', 'https://');
    }
    return this.avatarUrl;
  }

  get hasValidLinkedinUserToken(): boolean {
    if (!this.linkedinTokenExpiresTime) {
      return false;
    }

    const isLinkedinUserTokenExpired =
      !isBlank(this.linkedinTokenExpiresTime) && this.linkedinTokenExpiresTime < timestamp();
    return !isBlank(this.linkedinToken) && !isLinkedinUserTokenExpired;
  }

  membershipFor(group: GroupModel): MembershipModel | undefined {
    return this.memberships.find((membership) => membership.get('group').get('id') === group.get('id'));
  }

  ciamInfo = memberAction<undefined, { can_user_update_email: boolean }>({ path: 'ciam_info', type: 'GET' });

  updateEmail = memberAction<UpdateEmailPayload, undefined>({ path: 'update_email', type: 'PUT' });
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    user: UserModel;
  }
}
